import { createBrowserRouter, type RouteObject } from 'react-router';

import { placeholderCopy } from '@/content';

import { RootLayout } from './layouts/RootLayout';
import {
  CERTIFICATION_ASSESSMENT_PATH,
  CERTIFICATION_PATH,
  CLINIC_ROUTE,
  NAV_SECTIONS,
  NEW_PATIENT_PATH,
  PATIENT_ROUTE,
  PATIENTS_PATH,
  PRESCRIBERS_PATH,
  RESOURCES_PATH,
  SETTINGS_PATH,
  TASKS_PATH,
} from './navigation';
import { RouteErrorScreen } from './RouteErrorScreen';
import { ONBOARDING_NPI_PATH, RequireAccount, RequireNoAccount } from './session';

/**
 * Screens are lazy so the initial bundle is the shell plus whatever route the user landed on.
 * Access follows GET /me: login is public, the NPI step is for signed-in users without an account yet, and
 * everything else needs an account.
 */
/** Signed-in pages in the navigation that aren't built yet: each shows its title and a "coming soon" note. */
const placeholderRoute = (path: string, title: string): RouteObject => ({
  path,
  lazy: async () => {
    const { PlaceholderScreen } = await import('@/screens/placeholder/PlaceholderScreen');
    return { Component: () => <PlaceholderScreen title={title} /> };
  },
});

const BUILT_NAV_PATHS: readonly string[] = [
  '/',
  TASKS_PATH,
  PATIENTS_PATH,
  PRESCRIBERS_PATH,
  RESOURCES_PATH,
  SETTINGS_PATH,
];

/** Signed-in pages that are linked but not built yet: the unbuilt nav items, New patient, a patient's and a clinic's page. */
const placeholderRoutes: RouteObject[] = [
  ...NAV_SECTIONS.flat()
    .filter((item) => !BUILT_NAV_PATHS.includes(item.href))
    .map((item) => placeholderRoute(item.href.slice(1), item.label)),
  placeholderRoute(NEW_PATIENT_PATH.slice(1), placeholderCopy.titles.newPatient),
  placeholderRoute(PATIENT_ROUTE.slice(1), placeholderCopy.titles.patient),
  placeholderRoute(CLINIC_ROUTE.slice(1), placeholderCopy.titles.clinic),
];

const appRoutes: RouteObject[] = [
  {
    path: '/',
    Component: RootLayout,
    ErrorBoundary: RouteErrorScreen,
    children: [
      {
        // Lazy like the screens: the layout imports Nexus, which must stay out of the initial bundle.
        lazy: async () => {
          const { PublicLayout } = await import('./layouts/PublicLayout');
          return { Component: PublicLayout };
        },
        children: [
          {
            path: 'login',
            lazy: async () => {
              const { LoginScreen } = await import('@/screens/login/LoginScreen');
              return { Component: LoginScreen };
            },
          },
          {
            // Same page frame as login, but only for signed-in users who haven't claimed an account yet.
            Component: RequireNoAccount,
            children: [
              {
                path: ONBOARDING_NPI_PATH.slice(1),
                lazy: async () => {
                  const { OnboardingNpiScreen } = await import('@/screens/onboarding/OnboardingNpiScreen');
                  return { Component: OnboardingNpiScreen };
                },
              },
            ],
          },
        ],
      },
      {
        Component: RequireAccount,
        children: [
          {
            // Lazy for the same reason as PublicLayout: it imports Nexus.
            lazy: async () => {
              const { SignedInLayout } = await import('./layouts/SignedInLayout');
              return { Component: SignedInLayout };
            },
            children: [
              {
                // A screen's error renders inside the layout, so the navigation stays. React Router clears it on
                // navigation. Lazy because the boundary renders through ContentLayout, which imports Nexus.
                lazy: async () => {
                  const { SignedInRouteError } = await import('./layouts/SignedInRouteError');
                  return { ErrorBoundary: SignedInRouteError };
                },
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { DashboardScreen } = await import('@/screens/dashboard/DashboardScreen');
                      return { Component: DashboardScreen };
                    },
                  },
                  {
                    // The form and its assessment share the certification's progress through this layout.
                    path: CERTIFICATION_PATH.slice(1),
                    lazy: async () => {
                      const { CertificationLayout } = await import('@/screens/certification/CertificationLayout');
                      return { Component: CertificationLayout };
                    },
                    children: [
                      {
                        index: true,
                        lazy: async () => {
                          const { CertificationScreen } = await import('@/screens/certification/CertificationScreen');
                          return { Component: CertificationScreen };
                        },
                      },
                      {
                        path: CERTIFICATION_ASSESSMENT_PATH.slice(CERTIFICATION_PATH.length + 1),
                        lazy: async () => {
                          const { AssessmentScreen } = await import('@/screens/certification/AssessmentScreen');
                          return { Component: AssessmentScreen };
                        },
                      },
                    ],
                  },
                  {
                    path: TASKS_PATH.slice(1),
                    lazy: async () => {
                      const { TasksScreen } = await import('@/screens/tasks/TasksScreen');
                      return { Component: TasksScreen };
                    },
                  },
                  {
                    path: PATIENTS_PATH.slice(1),
                    lazy: async () => {
                      const { PatientsScreen } = await import('@/screens/patients/PatientsScreen');
                      return { Component: PatientsScreen };
                    },
                  },
                  {
                    path: PRESCRIBERS_PATH.slice(1),
                    lazy: async () => {
                      const { PrescribersScreen } = await import('@/screens/prescribers/PrescribersScreen');
                      return { Component: PrescribersScreen };
                    },
                  },
                  {
                    path: RESOURCES_PATH.slice(1),
                    lazy: async () => {
                      const { ResourcesScreen } = await import('@/screens/resources/ResourcesScreen');
                      return { Component: ResourcesScreen };
                    },
                  },
                  {
                    path: SETTINGS_PATH.slice(1),
                    lazy: async () => {
                      const { SettingsScreen } = await import('@/screens/settings/SettingsScreen');
                      return { Component: SettingsScreen };
                    },
                  },
                  ...placeholderRoutes,
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const createAppRouter = () => createBrowserRouter(appRoutes);
